import React, { useState, useCallback, useEffect, useMemo } from "react";
import DoctorEditForm from "./DoctorEditForm";
import axios from "axios";
import { API_ENDPOINTS, axiosInstance } from "config/api";
import { fetchDoctorsByClinic, fetchAllDoctors } from "services/doctorService";
import AddFrontdeskModal from "./AddFrontdeskModal";
import { SocketProvider } from "context/SocketProvider";
import {
  Box,
  Button,
  IconButton,
  useDisclosure,
  useColorModeValue,
  Flex,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormLabel,
  Input,
  Divider,
  Text,
  useToast,
  Select,
} from "components-lib";
import { FiEdit, FiTrash2, FiRefreshCcw } from "react-icons/fi";
import DoctorForm from "./DoctorForm";
import ComplexTable from "views/admin/default/components/ComplexTable";
import AddClinicModal from "./AddClinicModal";
import { formatTime12Hour } from "utils/dateUtils";
import { createFrontdeskFormDefaults } from "views/admin/shared/formDefaults";
import { useGlobalFilters } from "contexts/GlobalFiltersContext";

function formatOPDTiming(timing) {
  if (!timing) return "";
  if (typeof timing === "string" && timing.includes("-")) {
    const [start, end] = timing.split("-");
    return `${formatTime12Hour(start)} - ${formatTime12Hour(end)}`;
  }
  if (Array.isArray(timing)) {
    return timing
      .map(
        (slot) =>
          `${formatTime12Hour(slot.start)} - ${formatTime12Hour(slot.end)}`,
      )
      .join(", ");
  }
  return timing;
}

export default function DoctorsList() {
  const {
    isOpen: isDoctorFormOpen,
    onOpen: onDoctorFormOpen,
    onClose: onDoctorFormClose,
  } = useDisclosure();
  const {
    isOpen: isEditFormOpen,
    onOpen: onEditFormOpen,
    onClose: onEditFormClose,
  } = useDisclosure();

  const [doctorToEdit, setDoctorToEdit] = useState(null);
  const [doctorsData, setDoctorsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddClinicOpen, setIsAddClinicOpen] = useState(false);
  const [isAddFrontdeskOpen, setIsAddFrontdeskOpen] = useState(false);
  const [frontdeskForm, setFrontdeskForm] = useState(() =>
    createFrontdeskFormDefaults(),
  );
  const [frontdeskLoading, setFrontdeskLoading] = useState(false);
  const toast = useToast();
  const [clinicOptions, setClinicOptions] = useState([]);

  const globalFiltersContext = useGlobalFilters();

  // Extract global filters - call hooks first (before conditional return)
  const {
    filters: globalFilters = {},
    lists: globalLists = {},
    actions: globalActions = {},
  } = globalFiltersContext || {};

  const gClinicId = globalFilters?.clinicId ?? null;

  const clinics = globalLists?.clinics ?? [];

  const onOpenAddClinicModal = () => setIsAddClinicOpen(true);
  const onCloseAddClinicModal = () => setIsAddClinicOpen(false);
  const onOpenAddFrontdeskModal = () => setIsAddFrontdeskOpen(true);
  const onCloseAddFrontdeskModal = () => {
    setIsAddFrontdeskOpen(false);
    setFrontdeskForm(createFrontdeskFormDefaults());
  };

  const handleFrontdeskInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("bank_")) {
      const bankField = name.replace("bank_", "");
      setFrontdeskForm((prev) => ({
        ...prev,
        bank_details: {
          ...prev.bank_details,
          [bankField]: value,
        },
      }));
    } else {
      setFrontdeskForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddFrontdesk = async () => {
    setFrontdeskLoading(true);
    try {
      const body = {
        ...frontdeskForm,
        regdate: new Date().toISOString().split("T")[0],
        bank_details: `${frontdeskForm.bank_details.bank_name}, A/C: ${frontdeskForm.bank_details.account_number}, IFSC: ${frontdeskForm.bank_details.ifsc_code}`,
      };
      const response = await axiosInstance.post(
        "/api/frontdesk/register",
        body,
      );
      toast({
        title: "Success",
        description: "Frontdesk registered successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      setIsAddFrontdeskOpen(false);
      setFrontdeskForm(createFrontdeskFormDefaults());
    } catch (error) {
      console.error(
        "Error registering frontdesk:",
        error.response?.data || error,
      );
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to register frontdesk",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setFrontdeskLoading(false);
    }
  };

  const fetchDoctors = useCallback(async () => {
    try {
      // Use centralized doctor service with in-flight dedupe
      let data = [];
      if (gClinicId) {
        data = await fetchDoctorsByClinic(gClinicId, { force: false });
      } else {
        data = await fetchAllDoctors({ force: false });
      }

      if (data && Array.isArray(data)) {
        const transformedData = data.map((doctor) => ({
          id: doctor.id || "",
          doctorName: doctor.doctor || "",
          email: doctor.email || "",
          qualification: doctor.qualification || "",
          slot_duration: doctor.slot_duration || "",
          medical_license: doctor.medical_license || "",
          clinicName: doctor.clinic_name || "",
          address: doctor.address || "",
          languages: doctor.language ? (Array.isArray(doctor.language) ? doctor.language : [doctor.language]) : [],
          opdTiming: doctor.opd_timing || "",
          emergencyCharge: doctor.emergency_charge || 0,
          taxPercentage: doctor.tax_percentage || doctor.taxPercentage || 0,
          clinicIconURL: doctor.clinic_icon || "",
          createdAt: doctor.created_at || "",
        }));
        setDoctorsData(transformedData);
      } else {
        console.error("Invalid data format received from doctor service");
        setDoctorsData([]);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
      setDoctorsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [gClinicId]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const refreshDoctors = useCallback(() => {
    setIsLoading(true);
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    if (isAddFrontdeskOpen) {
      axiosInstance
        .get("api/clinic/allClinics")
        .then((res) => setClinicOptions(res.data || []));
    }
  }, [isAddFrontdeskOpen]);

  const handleEditClick = async (doctor, index) => {
    try {
      const response = await axiosInstance.get(
        `/api/doctor/profile?doctor_id=${doctor.id}`,
      );

      if (!response.data) {
        throw new Error("No data received from profile endpoint");
      }

      const completeDoctorData = {
        ...doctor,
        ...response.data,
        index,
      };

      setDoctorToEdit(completeDoctorData);
      onEditFormOpen();
    } catch (error) {
      console.error("Error fetching doctor profile:", error);
      setDoctorToEdit({ ...doctor, index });
      onEditFormOpen();
    }
  };

  const handleUpdateDoctor = (updatedDoctor) => {
    setDoctorsData((prev) =>
      prev.map((doc, i) => (i === updatedDoctor.index ? updatedDoctor : doc)),
    );
  };

  const handleAddDoctor = (newDoctor) => {
    setDoctorsData((prev) => [...prev, newDoctor]);
  };

  const handleDeleteDoctor = async (doctorId) => {
    try {
      const response = await axiosInstance.delete(
        `${API_ENDPOINTS.DOCTOR.DELETE}/${doctorId}`,
      );
      if (response.status === 200) {
        setDoctorsData((prev) =>
          prev.filter((doctor) => doctor.id !== doctorId),
        );

        // Show success toast
        toast({
          title: "Doctor Deleted",
          description: "Doctor has been successfully deleted from the system.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      console.error("Error deleting doctor:", error);

      // Show error toast
      toast({
        title: "Delete Failed",
        description:
          error.response?.data?.message ||
          "Failed to delete doctor. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  // Define columns for ComplexTable matching original doctors list fields
  const columnsData = useMemo(
    () => [
      {
        Header: "Photo",
        accessor: "clinicIconURL",
        Cell: ({ value }) => <Avatar size="sm" src={value || ""} />,
        disableSortBy: true,
      },
      {
        Header: "Name",
        accessor: "doctorName",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Slot Duration",
        accessor: "slot_duration",
      }, {
        Header: "Medical License",
        accessor: "medical_license",
      },
      {
        Header: "Qualification",
        accessor: "qualification",
      },
      {
        Header: "Clinic",
        accessor: "clinicName",
      },
      
      {
        Header: "Address",
        accessor: "address",
      },
      {
        Header: "Languages",
        accessor: "languages",
        Cell: ({ value }) => (value ? value : "Not specified"),
      },
      {
        Header: "OPD Timing",
        accessor: "opdTiming",
        Cell: ({ value }) => {
          try {
            let opdTimings = value;
            if (typeof value === "string") {
              opdTimings = JSON.parse(value);
            }
            const days = [
              "sunday",
              "monday",
              "tuesday",
              "wednesday",
              "thursday",
              "friday",
              "saturday",
            ];
            const today = days[new Date().getDay()];
            const todayTiming = opdTimings[today];
            if (
              !todayTiming ||
              todayTiming === "null" ||
              todayTiming.length === 0
            )
              return "Closed";
            return Array.isArray(todayTiming)
              ? todayTiming
                  .map((slot) => formatOPDTiming(`${slot.start}-${slot.end}`))
                  .join(", ")
              : formatOPDTiming(todayTiming);
          } catch (e) {
            return value || "Not specified";
          }
        },
      },
      {
        Header: "Emergency Charge",
        accessor: "emergencyCharge",
      },
      {
        Header: "Tax %",
        accessor: "taxPercentage",
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
      },
    ],
    [],
  );

  // Prepare table data with action buttons
  const tableData = useMemo(() => {
    return doctorsData.map((doc, index) => ({
      ...doc,
      actions: (
        <Flex>
          <IconButton
            aria-label="Edit Doctor"
            icon={<FiEdit />}
            size="sm"
            mr={2}
            onClick={() => handleEditClick(doc, index)}
            colorScheme="blue"
          />
          <IconButton
            aria-label="Delete Doctor"
            icon={<div style={{ fontSize: "1.5rem", color: "white" }}>X</div>}
            size="sm"
            colorScheme="red"
            onClick={() => handleDeleteDoctor(doc.id)}
          />
        </Flex>
      ),
    }));
  }, [doctorsData]);

  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  const hasGlobalFilters = !!globalFiltersContext;

  return hasGlobalFilters ? (
    <SocketProvider>
      <Box
        pt={{ base: "130px", md: "80px", xl: "80px" }}
        w="100%"
        overflowX="auto"
        bg={boxBg}
        p={4}
        borderRadius="md"
      >
        <Flex justify="space-between" mb="4" gap={4}>
          <Flex gap={4} align="start ">
            <Box>
              {/* <FormLabel>Filter by Clinic</FormLabel> */}
              <Select
                placeholder="All clinics"
                value={gClinicId ?? ""}
                onChange={(e) => {
                  const val = e.target.value || null;
                  if (typeof globalActions?.setClinicId === "function") {
                    globalActions.setClinicId(val);
                  }
                }}
                width="220px"
              >
                {clinics.map((c) => (
                  <option key={c.id || c.clinic_id || c._id} value={c.id || c.clinic_id || c._id}>
                    {c.name || c.clinic_name || c.clinic}
                  </option>
                ))}
              </Select>
            </Box>
            {/* <IconButton
              aria-label="Refresh doctors list"
              icon={<FiRefreshCcw />}
              onClick={refreshDoctors}
              isRound
              isDisabled={isLoading}
              alignSelf="end"
            /> */}
          </Flex>
          <Flex gap={4}>
            <Button
              colorScheme="orange"
              variant="solid"
              onClick={onOpenAddClinicModal}
            >
              Add Clinic
            </Button>
            <Button colorScheme="teal" onClick={onDoctorFormOpen}>
              Add Doctor
            </Button>
            <Button
              colorScheme="purple"
              variant="solid"
              onClick={onOpenAddFrontdeskModal}
            >
              Add Frontdesk
            </Button>
          </Flex>
        </Flex>
        <ComplexTable
          columnsData={columnsData}
          tableData={tableData}
          setTableDataComplex={setDoctorsData}
        />
        <DoctorForm
          isOpen={isDoctorFormOpen}
          
          onClose={onDoctorFormClose}
          handleAddDoctor={handleAddDoctor}
          preselectedClinicId={gClinicId}
        />
        <DoctorEditForm
          isOpen={isEditFormOpen}
          onClose={onEditFormClose}
          doctor={doctorToEdit}
          onUpdate={handleUpdateDoctor}
        />
        {/* Add Clinic Modal */}
        <AddClinicModal
          isOpen={isAddClinicOpen}
          onClose={onCloseAddClinicModal}
        />
        <AddFrontdeskModal
          isOpen={isAddFrontdeskOpen}
          onClose={onCloseAddFrontdeskModal}
          frontdeskForm={frontdeskForm}
          setFrontdeskForm={setFrontdeskForm}
          handleAddFrontdesk={handleAddFrontdesk}
          frontdeskLoading={frontdeskLoading}
          preselectedClinicId={gClinicId}
        />
      </Box>
    </SocketProvider>
  ) : (
    <Box p={4}>
      <Text color="red.500">
        Error: GlobalFiltersProvider not found. Please ensure AdminLayout wraps this component.
      </Text>
    </Box>
  );
}
